<template>
  <div>
    <div class="flex border-t border-b border-dark lg:px-12 flex-col lg:flex-row">
      <!-- Search -->
      <div class="flex items-center gap-4 justify-between lg:ps-0 px-4">
        <form @submit.prevent="searchEvents" class="flex items-center">
          <label for="search" class="cursor-pointer pr-4">
            <WellIcon icon="search-md" :size="24" />
          </label>
          <input
            id="search"
            class="bg-transparent border-none placeholder-dark focus:outline-none py-4"
            v-model="search"
            type="text"
            placeholder="SEARCH"
          />
        </form>
        <span class="cursor-pointer relative" @click="toggleDrawer">
          <WellIcon icon="settings-03" :size="24" type="custom-icons" />
          <span
            class="absolute bg-dark text-primary-contrast rounded-full w-5 h-5 top-[-7px] right-[-7px] flex items-center justify-center text-[10px] font-semibold"
            v-if="numberOfAppliedFilters > 0"
          >
            {{ numberOfAppliedFilters }}
          </span>
        </span>
      </div>
    </div>
    <Drawer
      id="filter-drawer"
      v-model:visible="isDrawerOpen"
      position="right"
      header="Filters"
      @close="toggleDrawer"
    >
      <button
        v-if="showDatePicker"
        class="button-dark-outlined w-full button-mobile flex items-center gap-3"
        @click="datePickerPopover.toggle"
      >
        <WellIcon icon="calendar" :size="16" />
        <span v-if="!date">
          DATE
        </span>
        <span v-else class="flex items-center gap-2">
          <span class="whitespace-nowrap">
            {{ $dayjs(date).format('MMM DD, YYYY') }}
          </span>
          <WellIcon
            class="cursor-pointer"
            icon="x"
            @click.stop="clearDate"
          />
        </span>
      </button>
      <Popover ref="datePickerPopover">
        <DatePicker
          v-model="date"
          inline
          @date-select="dateSelected"
        />
      </Popover>

      <div
        class="flex items-center justify-between gap-2 mt-7"
        v-if="showIncludePastEvents"
      >
        <span class="whitespace-nowrap">
          Show On-Demand Experiences
        </span>
        <ToggleSwitch
          :model-value="includePastEvents"
          @update:model-value="toggleIncludePastEvents"
        />
      </div>

      <span class="inline-block mt-8 mb-5">
        TAGS
      </span>
      <div class="flex flex-col gap-3">
        <button
          class="button-dark-outlined w-full button-mobile"
          :class="{ 'button-light-outlined': selectedCategoriesIds.includes(category.id) }"  
          v-for="category in categories"
          :key="category.id"
          @click="toggleCategory(category)"
        >
          {{ category.name }}
        </button>
      </div>
      <span class="inline-block mt-8 mb-5">
        SORT BY
      </span>
      <div class="flex flex-col gap-3">
        <button
          class="button-dark-outlined w-full button-mobile"
          v-for="option in orderingOptions"
          :key="option.value"
          @click="updateOrdering(option.value)"
          :class="{ 'button-light-outlined': ordering === option.value }"
        >
          {{ option.label }}
        </button>
      </div>
      <div class="flex flex-col justify-end h-32">
        <span
          class="flex justify-center items-center underline fixed bottom-0 bg-primary-contrast py-4 shadow-t-md -mx-4 w-full"
          @click="clearAll"
          v-if="!!numberOfAppliedFilters"
        >
          CLEAR ALL
        </span>
      </div>
    </Drawer>
  </div>
</template>

<script setup lang="ts">
import WellIcon from '@/components/common/WellIcon.vue'

const emit = defineEmits(['toggleCategory', 'searchEvents', 'clearDate', 'selectOrdering', 'clearAll', 'toggleIncludePastEvents'])

const isDrawerOpen = ref(false)
const datePickerPopover = ref();
const search = defineModel('search')
const date = defineModel('date')
const includePastEvents = defineModel('includePastEvents')

function toggleDrawer() {
  isDrawerOpen.value = !isDrawerOpen.value
}

function toggleCategory(category: EventCategoriesResponse) {
  emit('toggleCategory', category)
}

function searchEvents() {
  emit('searchEvents', search.value)
}

function clearDate() {
  emit('clearDate')
}

function dateSelected(event: any) {
  datePickerPopover.value.hide()
}

function clearAll() {
  emit('clearAll')
}

function updateOrdering(ordering: OrderingType) {
  emit('updateOrdering', ordering)
}

function toggleIncludePastEvents() {
  emit('toggleIncludePastEvents')
}

const props = withDefaults(defineProps<{
  ordering: OrderingType
  orderingOptions: OrderingOptions[]
  categories: EventCategoriesResponse[]
  selectedCategoriesIds: EventCategoriesResponse[]
  showDatePicker: boolean
  isAnyFilterActive: boolean
  numberOfAppliedFilters: number
  includePastEvents?: boolean
  showIncludePastEvents?: boolean
}>(), {
  showDatePicker: true,
  showIncludePastEvents: true,
});
</script>

<style>
#filter-drawer {
  --p-drawer-background: var(--primary-contrast);
  --p-button-text-secondary-color: var(--dark);
  --p-drawer-title-font-weight: 400;
  --p-drawer-title-font-size: 16px;
  --p-drawer-content-padding: 2rem 1rem;
  --p-drawer-header-padding: 1rem;
  .p-drawer-header {
    border-bottom: 1px solid var(--dark);
  }

  .p-drawer-content {
    padding-bottom: 0;
  }

  .p-drawer-close-button {
    justify-content: flex-end;
  }
}
</style>
